import { useEffect, useRef, useState, useCallback, useMemo, Fragment, type FormEvent, type CSSProperties, type MouseEvent as ReactMouseEvent } from 'react';
import { api } from '../shell/bridge';
import type { ThreadMessage, LocalEcho } from '../hooks/useThread';
import type { Role } from '../lib/api';
import { SkeletonBlock } from '../ui/Skeleton';
import { Overlay } from '../ui/Overlay';
import { tabListKeyDown } from '../ui/tabs';
import { ComposeMenu, TILE_DEFS, type ComposeTile } from '../components/ComposeMenu';
import { Composer } from '../components/Composer';
import { LifeRail } from '../components/LifeRail';
/* The cadence-nudge payload is written by the Durable Object and read here —
   one parser in shared/ so the two can never drift (card yejoF8uTVxyf). Same
   precedent as LifeRail's shared/life-rail.ts. Pulling the shared graph into
   this bundle is why web/tsconfig.json carries allowImportingTsExtensions —
   the shared modules import each other with the .ts extension, the convention
   the root tsconfig already allows. */
import { searchExcerpt } from '../lib/searchExcerpt';
import { parseDateNudge } from '../../../shared/date-nudge';
/* One-tap Today (hSgJtaF6deWm): the nudge card's quick-picks are four of the
   hand-curated library, so the titles/notes come from the same source the
   concierge's suggestions use — one library, no copy drift. */
import { DATE_TEMPLATES, type DateTemplate } from '../../../shared/date-templates';
/* Card tPPB3ozS4YrK — the hint's lifetime, read from the same constant the
   room's comments quote, so "4s of silence" means one number everywhere. */
import { TYPING_TTL_MS } from '../../../shared/types';
import { TabHint } from '../components/TabHint';
import { markHintSeen } from '../lib/hints';
import { Tooltip } from '../ui/Tooltip';
/* Founder email 2026-10-27: the thread's reveal card and Reveal history share
   one presentation component rather than two lookalikes that drift. */
import { RevealPair } from '../components/RevealPair';
/* H7 (card ESidDNa3mmaP): every catch block below keeps its console.error
   for the log and ALSO announces itself through this channel, so a failed
   action never looks like a success. */
import { reportActionFailure } from '../lib/actionFeedback';
import { ActionErrorToast } from '../ui/ActionErrorToast';
import { markTodayCardVisible, logTodayAction } from '../lib/todayMetrics';
/* UX item 5 (H5/H6): the composer draft, the filter chip and the stream's
   scroll offset are component state, and this screen unmounts on every tab
   switch — so they are kept in the session bag and restored on the way back. */
import { useRemembered, useScrollMemory } from '../hooks/useUiMemory.ts';
import type { Dest } from '../App';
import { useKeyboardInset } from '../hooks/useKeyboardInset';
import '../styles/thread.css';
/* Desktop uplift A1b (card sUIMDZCfzM0l) — the right context rail reuses the
   notebook .nb-context primitive (composition, no new design). */
import '../styles/thread-rail.css';

/* Kudos glyph (card svmgRALhXjiq): the 🫂 emoji rendered per-platform and sat
   outside the vector icon set the rest of the app uses. This is the same heart
   the compose menu's Kudos tile already draws, so one glyph per action. */
const KUDOS_GLYPH = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21.2l7.8-7.8 1-1a5.5 5.5 0 0 0 0-7.8z" />
  </svg>
);

/* Card-family glyphs (card KOFFfNfIwDq1, founder email 2026-10-22): the game
   round card drew 🎲 and the question card ❓ — text glyphs that render
   per-platform and sit outside the vector set the rest of the app draws. Both
   are the real Lucide shapes (tic-tac-toe, circle-question-mark, lucide-static
   v1.61.0), inlined the same way the concierge chip inlines sparkles — no icon
   dependency. Same box as KUDOS_GLYPH (20px, 1.8 stroke) so the three cards in
   this slot keep one weight down the stream. */
const GAME_GLYPH = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M12 2v20" />
    <path d="m21 16-5 5" />
    <path d="m21 21-5-5" />
    <path d="M22 12H2" />
    <path d="M8 3 3 8" />
    <path d="M8 8 3 3" />
    <circle cx="18.5" cy="5.5" r="2.5" />
    <circle cx="5.5" cy="18.5" r="2.5" />
  </svg>
);

const QUESTION_GLYPH = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <path d="M12 17h.01" />
  </svg>
);

/** Mirrors MAX_THREAD_TEXT in shared/security.ts — the web bundle types its
 *  own surface rather than importing shared/ (same convention as the other
 *  mirrored limits). The server rejects anything longer with 'too_long'; this
 *  stops it before it is sent (card YYrJc5OkW7EP). scripts/probe-security.mjs
 *  asserts both halves agree. */
const THREAD_TEXT_MAX = 4000;

/* Day separators (card 4ynbWszGdqUd): the stream is newest-at-top, so a
   separator labels the day of the messages BELOW it. It is inserted whenever a
   message's calendar day differs from the message above it. Keys are local
   YYYY-MM-DD so a couple in the same timezone sees the same groupings. */
function dayKey(ts?: number): string | null {
  if (typeof ts !== 'number' || !Number.isFinite(ts)) return null;
  const d = new Date(ts);
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}
function dayLabel(ts?: number): string {
  if (typeof ts !== 'number' || !Number.isFinite(ts)) return '';
  const d = new Date(ts);
  const today = new Date();
  const yest = new Date(today.getTime() - 86_400_000);
  const same = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (same(d, today)) return 'Today';
  if (same(d, yest)) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

/* ---------------------------------------------------------------------------
   Props — optional onComposePick (App wires it; no-op default so Thread
   compiles standalone without App.tsx changes).
   --------------------------------------------------------------------------- */

interface ThreadProps {
  messages: ThreadMessage[];
  echoes: LocalEcho[];
  roleFor: (memberId: string | null | undefined) => Role | null;
  /** The sender's display name (card yFLCjm7ONIEc) — the partner's messages
   *  are labelled with it, so a name change shows on the next message. */
  nameFor?: (memberId: string | null | undefined) => string | null;
  /** The sender's picked reply-text colour (card 9QADJv3CdiOh), already
   *  resolved to a hex by the caller. Null (or absent) = no pick / unknown
   *  key — the bubble keeps its role-token ink. Only the partner's side ever
   *  receives a colour: your own words stay on the cream bubble. */
  colourFor?: (memberId: string | null | undefined) => string | null;
  myMemberId: string | null;
  /** Card tPPB3ozS4YrK — a keystroke signal, already throttled to one frame
   *  per 2s by useThread. Optional so the standalone probe mount, which has no
   *  socket behind it, keeps compiling untouched. */
  onTyping?: () => void;
  /** PEAR_TYPING as /api/auth/config reported it. `=== true` is the only value
   *  that sends a frame OR renders the indicator — the client half of a flag
   *  whose server half stops relaying the moment it is off. */
  typingOn?: boolean;
  /** Second arg = the composer chip was armed (invoke path, card hphiEFLMogbg).
   *  Returns false when the message never left this device (socket closed /
   *  send threw) — the composer puts the draft back instead of losing it (H7). */
  onSend: (text: string, wantsAgent?: boolean) => boolean;
  /** Delete your own message (card — msg-remove): own-text trash affordance
   *  + native long-press on touch. The DO refuses anything not self-authored,
   *  so a stale seq here is a quiet no-op. Optional. */
  onRemoveMessage?: (seq: number) => void;
  /** Skeleton while the first history page is in flight. */
  loading: boolean;
  /** Called when a compose-tile is picked from the + menu. Optional. */
  onComposePick?: (tile: ComposeTile) => void;
  /** Compose menu deep-link (design v1.1 §5.2): opens the kudos sender. */
  composeIntent?: { kind: 'kudos' } | null;
  /** One-shot intent (card 4vIN4UouHPz4): App clears composeIntent the moment
   *  a panel has acted on it. Without this the intent survives every unmount,
   *  so Thread's mount effect re-fires on each return and resurrects the
   *  kudos sender on whatever screen the user is now on. */
  onIntentConsumed?: () => void;
  /** Explicit navigation (card Mn55eOvFgvC0, H4): every thread card that
   *  links to another screen calls this with its own destination — section,
   *  subsection, optional entity — instead of a generic "go to Us". */
  onNavigate?: (dest: Dest) => void;
  /** Game-round card "Open in Us" (card Bkt2clm8SSOI): names the round so the
   *  Us > Game destination opens THAT round (H4). */
  onOpenGameRound?: (roundId: string) => void;
  /** Quick link from the More menu (card yOcdjnR6pXBz): land on Thread with
   *  this filter already selected. null = no request, keep current. */
  requestedFilter?: 'all' | 'chat' | null;
  /** Two members have joined — the invite already landed. Drives the empty
   *  state: a paired couple hears "say hello", a solo creator still gets
   *  the point-back-to-the-pairing-screen copy. Defaults to false so the
   *  probe and standalone use keep the invite wording. */
  paired?: boolean;
  /** Windowed hydration (card 4ynbWszGdqUd): true when the server holds
   *  messages older than the loaded window — shows the expand affordance. */
  hasOlder?: boolean;
  /** Fetch the next-older page (no window). Wired to the expand affordance. */
  onLoadOlder?: () => void;
  /** Archive search (card 2sjo3uNvaRDr): server-side substring match over the
   *  couple's whole thread. Resolves null on failure so the panel can say so
   *  instead of showing an empty list that reads as "no results". */
  onSearch?: (query: string) => Promise<ThreadMessage[] | null>;
  /** Page history so `seq` is loaded (card 2sjo3uNvaRDr). False when the
   *  socket could not take it — the result list stays up rather than closing
   *  on a jump that never happened. */
  onJumpToSeq?: (seq: number) => boolean;
}

/**
 * The couple thread. Renders messages declaratively — React escapes text, so
 * the `escapeHtml` + `innerHTML` pair from main.ts is gone entirely.
 *
 * Home-stream variant (card B):
 *   - Pinned "Tonight" card at top
 *   - TOP-DOCKED composer with ComposeMenu (dZjN98QXAgEH) — respond first
 *   - Filter chips row (All / Chat / Asks / Questions)
 *   - Invite-flavoured empty state
 *
 * Comprehension order is respond > read (card dZjN98QXAgEH, Jonathan
 * 2026-10-12): the composer sits directly under the welcome prompt and the
 * message stream renders BELOW it. The old bottom dock is gone.
 */
export function Thread({ messages, echoes, roleFor, nameFor, colourFor, myMemberId, onTyping, typingOn = false, onSend, onRemoveMessage, loading, onComposePick, composeIntent, onIntentConsumed, onNavigate, onOpenGameRound, requestedFilter, paired = false, hasOlder = false, onLoadOlder, onSearch, onJumpToSeq }: ThreadProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  /* A half-written message survives leaving the tab (UX item 5). It is cleared
     in the same places it always was — on send, and on a send the socket could
     not take. */
  const [draft, setDraft] = useRemembered('thread.draft', '');
  /* Restores where you were reading. A reader parked at the bottom is left to
     the auto-scroll below (new messages still pull the stream down). */
  useScrollMemory(scrollRef, 'thread');

  /* Grow the box with the draft is the shared Composer's half now (card
     4DrBwftybJ7q) — the Thread passes its ref and the CSS keeps the floors:
     min-height 72px and the 40vh cap. */

  /* ── Kudos (card V0dtVf36Dplz) ─────────────────────────────────────
     composeIntent opens a small "say that was lovely" sender; POSTs to
     /api/chat/kudos, whose appendMessage broadcasts to both partners.
     A received kudos (fromMemberId ≠ me) triggers confetti. */
  const [kudosOpen, setKudosOpen] = useState(false);
  const [kudosNote, setKudosNote] = useState('');
  const [kudosBusy, setKudosBusy] = useState(false);
  /* Two INDEPENDENT pieces of state (sol review M4): the burst the partner's
     kudos earns, and the small echo the sender sees on their own card. They
     used to share one seq, so an outgoing kudos was compared against a
     sequence picked from INCOMING ones — the sender never saw their echo. */
  const [celebrateSeq, setCelebrateSeq] = useState<number | null>(null);
  const [echoSeq, setEchoSeq] = useState<number | null>(null);
  /* Celebration is event-based, never history-based: the highest seq present
     on the first render is the baseline, and only a kudos ABOVE it counts as
     live. Remounting the screen used to re-fire the burst for old kudos. */
  const kudosBaselineRef = useRef<number | null>(null);
  const kudosHandledRef = useRef<Set<number>>(new Set());
  const celebrateTimerRef = useRef<number | null>(null);
  const echoTimerRef = useRef<number | null>(null);
  /* prefers-reduced-motion gets a quiet static line instead of the particle
     layer — the JSX promised one for a long time without rendering it. */
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReduceMotion(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (composeIntent?.kind === 'kudos') { setKudosOpen(true); setKudosNote(''); onIntentConsumed?.(); }
  }, [composeIntent, onIntentConsumed]);

  /* The kudos postcard is portalled to body (card DpcUGvfaDJir), so the
     --kb-inset App writes on #app never reaches it — read the visualViewport
     here too, or the keyboard covers the panel the moment the note field
     takes focus. Same source as useKeyboardInset in App.tsx. */
  const kudosKbInset = useKeyboardInset();

  // Escape, focus and the inert background now come from the shared Overlay
  // primitive (UX item 6) — the postcard's own window listener is gone, so
  // there is one Escape contract for every surface instead of one per dialog.

  // A kudos from the partner celebrates ONCE, when it arrives (persisted
  // history never does), and its own timer is never cancelled by a re-run of
  // this effect — celebrateSeq is deliberately NOT a dependency (sol M4).
  useEffect(() => {
    const maxSeq = messages.reduce((n, m) => Math.max(n, m.seq), 0);
    if (kudosBaselineRef.current === null) {
      kudosBaselineRef.current = maxSeq;
      return;
    }
    const fresh = messages.filter(
      (m) => m.kind === 'kudos' && m.seq > (kudosBaselineRef.current ?? 0) && !kudosHandledRef.current.has(m.seq),
    );
    if (!fresh.length) return;
    for (const m of fresh) kudosHandledRef.current.add(m.seq);

    const theirs = fresh.filter((m) => m.fromMemberId !== null && m.fromMemberId !== myMemberId).pop();
    if (theirs) {
      setCelebrateSeq(theirs.seq);
      if (celebrateTimerRef.current !== null) window.clearTimeout(celebrateTimerRef.current);
      celebrateTimerRef.current = window.setTimeout(() => {
        setCelebrateSeq(null);
        celebrateTimerRef.current = null;
      }, 1600);
    }

    const mine = fresh.filter((m) => m.fromMemberId === myMemberId).pop();
    if (mine) {
      setEchoSeq(mine.seq);
      if (echoTimerRef.current !== null) window.clearTimeout(echoTimerRef.current);
      echoTimerRef.current = window.setTimeout(() => {
        setEchoSeq(null);
        echoTimerRef.current = null;
      }, 1600);
    }
  }, [messages, myMemberId]);

  useEffect(
    () => () => {
      if (celebrateTimerRef.current !== null) window.clearTimeout(celebrateTimerRef.current);
      if (echoTimerRef.current !== null) window.clearTimeout(echoTimerRef.current);
    },
    [],
  );

  const sendKudos = async () => {
    if (kudosBusy) return;
    setKudosBusy(true);
    try {
      await api('/api/chat/kudos', { body: kudosNote.trim() ? { note: kudosNote.trim() } : {} });
      setKudosOpen(false);
      setKudosNote('');
    } catch (e) {
      console.error('[Pear] kudos failed', e);
      reportActionFailure('send the kudos');
    } finally {
      setKudosBusy(false);
    }
  };

  /* Invoke-path chip (card hphiEFLMogbg). Shown only when BOTH hold:
     - the couple chose `invoke` — an `auto` couple has no chip trigger
     - at least one AI permission is on, so a tap can actually succeed
     Two small GETs on mount; the server re-checks both regardless, so a stale
     value here can only waste a tap, never produce a wrong card. */
  const [showChip, setShowChip] = useState(false);
  // Persistent concierge toggle (card DvrFWDcNA9Ix, folds 6wz9UQwCi5ND): the
  // armed state survives a send and a remount (session bag, per thread) so it
  // stays on until the user turns it off — no more one-shot disarm.
  const [armed, setArmed] = useRemembered('thread.agentArmed', false);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [s, t] = await Promise.all([
          api<{ settings: { mode: string } }>('/api/ai/agent-settings'),
          api<{ toggles: Record<string, boolean> }>('/api/ai/toggles'),
        ]);
        if (!live) return;
        const anyOn = Object.values(t.toggles ?? {}).some(Boolean);
        setShowChip(s.settings?.mode === 'invoke' && anyOn);
      } catch (err) {
        console.error('[Pear] agent chip context failed', err);
        if (live) setShowChip(false);
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  // ---------- Filter chips ----------
  const [activeFilter, setActiveFilter] = useRemembered<'all' | 'chat' | 'asks' | 'questions'>('thread.filter', 'all');

  useEffect(() => {
    if (requestedFilter) setActiveFilter(requestedFilter);
  }, [requestedFilter]);

  /* Real filters (sol review M3): "Chat only" used to drop agent cards alone,
     so asks, questions, dates and kudos all still showed under a chip that
     promised conversation. Chat is now the couple's own words — messages and
     kudos — and every operational card is out of it; Asks and Questions are
     live rather than "coming soon" placeholders. */
  const filteredMessages = useMemo(() => {
    switch (activeFilter) {
      case 'chat':
        return messages.filter((msg) => msg.kind !== 'agent-card' && msg.kind !== 'request-card' && msg.kind !== 'question-card' && msg.kind !== 'date-card');
      case 'asks':
        return messages.filter((msg) => msg.kind === 'request-card');
      case 'questions':
        return messages.filter((msg) => msg.kind === 'question-card');
      default:
        return messages;
    }
  }, [messages, activeFilter]);

  /* Reverse chronological (card BvSZRPd9uuwW, founder email 2026-10-19):
     newest at top. Render-only — useThread keeps ascending seq because the
     `before:` paging, the kudos baseline and the echo/ack logic all read
     ascending order; flipping the ARRAY here changes what you see and
     nothing that reads history. */
  const renderMessages = useMemo(() => [...filteredMessages].reverse(), [filteredMessages]);

  // One request is ONE card in the stream: the lifecycle emits a message per
  // action, so every request-card a later one supersedes is dropped — the
  // resolution replaces the ask instead of leaving a stale Accept button
  // beside it (2eueFMxFylie).
  const supersededRequestSeqs = useMemo(() => {
    const latestByRequest = new Map<string, number>();
    for (const msg of filteredMessages) {
      if (msg.kind !== 'request-card') continue;
      const id = parseRequestCard(msg.text)?.requestId;
      if (id) latestByRequest.set(id, msg.seq);
    }
    const dead = new Set<number>();
    for (const msg of filteredMessages) {
      if (msg.kind !== 'request-card') continue;
      const id = parseRequestCard(msg.text)?.requestId;
      if (id && latestByRequest.get(id) !== msg.seq) dead.add(msg.seq);
    }
    return dead;
  }, [filteredMessages]);

  // Today's question is ONE card in the stream, not one per day. The DO keeps a
  // row per question it has ever carded (couple-room.ts syncQuestionCard) and
  // every one of them renders the label "Today's question", so the same line
  // stacked up day after day (founder report 2026-10-08). Keep the newest card
  // and drop the ones it supersedes; the Us screen's Reveal history still holds
  // the past questions, which is where they belong.
  const supersededQuestionSeqs = useMemo(() => {
    let latest = -1;
    for (const msg of filteredMessages) {
      if (msg.kind === 'question-card' && parseQuestionCard(msg.text)) latest = msg.seq;
    }
    const dead = new Set<number>();
    if (latest < 0) return dead;
    for (const msg of filteredMessages) {
      if (msg.kind === 'question-card' && msg.seq !== latest) dead.add(msg.seq);
    }
    return dead;
  }, [filteredMessages]);

  /* The "Needs attention" region used to live here (sol review item 10): a
     compact strip above the stream that re-named the cards waiting on the
     reader. Jonathan 2026-10-17 cut it — the answer card is already IN the
     stream, so the strip was the same content twice. The stream is the one
     place a waiting card waits; nothing else was moved (card G2a36nBM74sD). */

  // ---------- ComposeMenu state ----------
  const [menuOpen, setMenuOpen] = useState(false);

  /* ── Typing (card tPPB3ozS4YrK) ───────────────────────────────────────
     The indicator lives HERE, in the open thread, and nowhere else: no thread
     list row, no tab bar, no drawer preview — the card's calm constraints say
     a keystroke is a fact about one conversation, not a couple-wide status.

     Three independent gates, because each one is a different promise:
       typingOn  — the worker's PEAR_TYPING, `=== true`; a missing or false
                   config never puts the element in the DOM at all;
       from !== myMemberId — the room already relays partner-only, this is the
                   second belt in case an echo ever reaches the sender;
       the 4s timer — silence clears it, counted from ARRIVAL here, never from
                   the sender's clock. */
  const [typingFrom, setTypingFrom] = useState<string | null>(null);
  const typingTimerRef = useRef<number | null>(null);
  useEffect(() => {
    const onTyping = (ev: Event) => {
      if (!typingOn) return;
      const from = (ev as CustomEvent<{ memberId?: unknown }>).detail?.memberId;
      if (typeof from !== 'string' || (myMemberId && from === myMemberId)) return;
      setTypingFrom(from);
      if (typingTimerRef.current !== null) window.clearTimeout(typingTimerRef.current);
      typingTimerRef.current = window.setTimeout(() => {
        setTypingFrom(null);
        typingTimerRef.current = null;
      }, TYPING_TTL_MS);
    };
    window.addEventListener('pear:typing', onTyping as EventListener);
    return () => {
      window.removeEventListener('pear:typing', onTyping as EventListener);
      if (typingTimerRef.current !== null) window.clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    };
  }, [typingOn, myMemberId]);
  // Flag switched off while a hint was showing (a config refetch, a worker
  // restart): the element goes at once rather than living out its 4s window.
  useEffect(() => {
    if (!typingOn) {
      setTypingFrom(null);
      if (typingTimerRef.current !== null) window.clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
  }, [typingOn]);

  /* ── Archive (card 2sjo3uNvaRDr) ────────────────────────────────────
     The stream is a 7-day window, so "the message from October" is not
     reachable by scrolling. This is the find affordance: a server-side
     search over the couple's whole thread, and a jump that pages history to
     the row you pick.

     Three states that must not be confused, because each is a different
     truth to the reader: no results (the word is not there), a failure
     (search could not run — onSearch resolves null), and not-supported
     (the caller passed no onSearch, which happens in the standalone probe
     mount). Collapsing failure into "no results" would tell a user their
     memory is wrong when the server was simply unreachable. */
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveQuery, setArchiveQuery] = useState('');
  const [archiveResults, setArchiveResults] = useState<ThreadMessage[] | null>(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  /* ── Desktop rail selection (card sUIMDZCfzM0l, A1b) ──────────────
     The right context rail is a desktop-only affordance: it reads the SAME
     >=1100px three-zone token A1a caps the stream at. Selection is a small
     state here — the rail itself is a sticky overlay inside #thread-scroll,
     rendered only while a message is selected AND the desktop token is live,
     so it is never an empty box on a phone. */
  const [selectedSeq, setSelectedSeq] = useState<number | null>(null);
  const railRef = useRef<HTMLElement | null>(null);
  const desktopMatch = useRef<MediaQueryList | null>(null);
  useEffect(() => {
    desktopMatch.current = window.matchMedia('(min-width: 1100px)');
  }, []);
  /* Esc closes the rail with the same Escape contract the shared Overlay
     uses; focus returns to the row that opened it. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedSeq !== null) {
        e.preventDefault();
        setSelectedSeq(null);
        const el = document.querySelector(`[data-msg-seq="${selectedSeq}"]`);
        if (el instanceof HTMLElement) el.focus();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [selectedSeq]);

  const selectedMsg = useMemo(
    () => (selectedSeq !== null ? filteredMessages.find((m) => m.seq === selectedSeq) ?? null : null),
    [selectedSeq, filteredMessages],
  );

  /* Desktop-only row selection: a tap on a message row (delegated to the
     scroll container so Bubble/card components stay untouched) selects it for
     the rail. Interactive children (buttons, links, inputs) are left alone —
     the rail's own controls and the in-stream affordances must still work. */
  const onThreadRowClick = useCallback(
    (e: ReactMouseEvent) => {
      if (desktopMatch.current?.matches !== true) return;
      if (e.target instanceof Element && e.target.closest('button, a, input, textarea, select, [role="tab"], .pear-msg-save, .pear-msg-trash')) return;
      const row = e.target instanceof Element
        ? e.target.closest<HTMLElement>('[data-msg-seq], [data-question-card-seq], [data-request-card-seq], [data-date-card-seq], [data-kudos-seq], [data-date-nudge-seq]')
        : null;
      const seq = row
        ? Number(
            row.getAttribute('data-msg-seq') ??
            row.getAttribute('data-question-card-seq') ??
            row.getAttribute('data-request-card-seq') ??
            row.getAttribute('data-date-card-seq') ??
            row.getAttribute('data-kudos-seq') ??
            row.getAttribute('data-date-nudge-seq'),
          )
        : NaN;
      if (Number.isFinite(seq)) {
        setSelectedSeq(seq);
        // Make the selected row focusable so Esc can return focus to it.
        row!.setAttribute('tabindex', '-1');
      }
    },
    [],
  );

  /* Opening the rail moves focus into it; closing restores the selecting row. */
  useEffect(() => {
    if (selectedSeq !== null && railRef.current) {
      const first = railRef.current.querySelector<HTMLElement>('[data-thread-rail-close], button, a, [tabindex]');
      if (first) first.focus();
    }
  }, [selectedSeq]);

  const runArchiveSearch = useCallback(async () => {
    if (!onSearch) return;
    const q = archiveQuery.trim();
    if (q.length < 2) {
      setArchiveResults(null);
      setArchiveError(null);
      return;
    }
    setArchiveBusy(true);
    setArchiveError(null);
    const found = await onSearch(q);
    setArchiveBusy(false);
    if (found === null) {
      setArchiveResults(null);
      setArchiveError('Search could not run — try again in a moment.');
      return;
    }
    setArchiveResults(found);
  }, [archiveQuery, onSearch]);

  /* Jump: page history so the target is loaded, then land on it. The scroll
     waits a beat because the history frame is a round trip — scrolling at
     once would find nothing and leave the reader at the top. If the socket
     could not take the request, say so and keep the list: a jump that
     silently did nothing is the worst of the three outcomes. */
  const jumpToResult = useCallback((seq: number) => {
    if (!onJumpToSeq) return;
    if (!onJumpToSeq(seq)) {
      setArchiveError('That message could not be loaded — reconnect and try again.');
      return;
    }
    setArchiveOpen(false);
    window.setTimeout(() => {
      const el = document.querySelector(`[data-msg-seq="${seq}"]`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'center', behavior: 'auto' });
        el.setAttribute('data-archive-jumped', 'true');
        window.setTimeout(() => el.removeAttribute('data-archive-jumped'), 2400);
      }
    }, 350);
  }, [onJumpToSeq]);

  const handleComposePick = useCallback(
    (tile: ComposeTile) => {
      onComposePick?.(tile);
      if (tile === 'message') {
        // Message tile closes menu and refocuses input
        setMenuOpen(false);
        inputRef.current?.focus();
      } else {
        // Other tiles: close menu, caller handles routing
        setMenuOpen(false);
      }
    },
    [onComposePick],
  );

  const count = messages.length + echoes.length;
  useEffect(() => {
    const el = scrollRef.current;
    // Stand aside while useScrollMemory is putting the reader back where they
    // were (UX item 5) — otherwise every mount yanks them off their place.
    // Newest-at-top (card BvSZRPd9uuwW): the new row lands at the TOP, so the
    // stream pins there instead of to the oldest message at the bottom.
    if (el && el.dataset.uiRestore !== '1') el.scrollTop = 0;
  }, [count]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    // Persistent toggle (DvrFWDcNA9Ix): the concierge invitation stays armed
    // for this and the next message until the user turns it off.
    // H7: the composer is already empty by here, so a send the socket could
    // not take must put the text back rather than drop it silently.
    const handed = onSend(text, armed && showChip);
    if (handed === false) setDraft(text);
    // No disarm here: the toggle (DvrFWDcNA9Ix) stays on until the user turns
    // it off. The invitation stays armed for the next message too.
  };

  // First-move hint (Rl0vzkZ2hdAe): retire the welcome line once the couple
  // has put something in the thread. The backend writes today's question into
  // the stream as a card, so a brand-new couple is never empty — that card is
  // not "use", it is furniture, and counting it dismissed the hint seconds
  // after load. Anything else (chat, an ask, kudos, an agent card) is the
  // thread being used; a pending local echo counts too, so the line goes the
  // moment you hit Enter rather than when the server acks.
  const hasContent = echoes.length > 0 || messages.some(m => m.kind !== 'question-card');

  // First-move hint (Rl0vzkZ2hdAe): the welcome line has done its job once
  // there is anything in the thread — remember it and stop drawing it.
  useEffect(() => {
    if (hasContent) markHintSeen('thread');
  }, [hasContent]);

  return (
    <>
      {/* Opening line, shortened (card I3nVYMBPlKHZ): one plain welcome, with
          the functional detail moved into a tooltip beside it — the wording
          Jonathan asked for was "Welcome to your thread, your starting point
          for today." Rl0vzkZ2hdAe: shown until the thread has content. */}
      <TabHint id="hint-thread" hintKey="thread">
        Welcome to your thread, your starting point for today.
        <Tooltip label="Everything you both say lives here, in order, on both phones. For example: say 'running 10 late' from the train and it is on their screen before they reach the door. The + opens Ask, Kudos and today's question; the chips below narrow what you see.">
          <span className="pear-tip-trigger" aria-hidden="true">ⓘ</span>
        </Tooltip>
      </TabHint>
      <div id="thread-scroll" className="pear-thread" ref={scrollRef} onClick={onThreadRowClick}>
        {/* The pinned "What would you like to remember tonight?" prompt is
            gone (card 56grGaVZY5BT): it was obtrusive and its Add memory
            button was a placeholder that dismissed rather than captured.
            Capture still lives in the compose menu's Note / Idea tiles. */}

        {/* ---- Filter chips ----
             A real tablist (UX item 6, H8): roving tabindex so the row is one
             tab stop, arrows move between the filters, and each chip names the
             region it filters — which is now a real tabpanel (#thread-panel),
             not the scroll box the chips themselves live in: a tab may not
             control an ancestor of its own tablist. Disabled chips (the ones
             still coming) stay out of both the order and the arrow sequence. */}
        <div className="pear-chips" role="tablist" aria-label="Thread filters">
          {filterChips.map((chip) => (
            <button
              key={chip.key}
              className={[
                'pear-filter-chip',
                !chip.disabled && activeFilter === chip.key ? 'is-active' : '',
                chip.disabled ? 'is-disabled' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              title={chip.disabled ? 'coming soon' : undefined}
              role="tab"
              id={`thread-chip-${chip.key}`}
              data-tab={chip.key}
              aria-selected={!chip.disabled && activeFilter === chip.key}
              aria-controls="thread-panel"
              tabIndex={!chip.disabled && activeFilter === chip.key ? 0 : -1}
              onKeyDown={e =>
                tabListKeyDown(e, el => {
                  const next = filterChips.find(c => String(c.key) === (el.dataset.tab ?? ''));
                  if (next && !next.disabled) setActiveFilter(next.key);
                })
              }
              disabled={!!chip.disabled}
              onClick={() => {
                if (!chip.disabled) setActiveFilter(chip.key);
              }}
            >
              {chip.label}
            </button>
          ))}
        </div>

        {/* ---- Archive (card 2sjo3uNvaRDr) ----
             Sits between the chips and the stream: it is a way INTO the
             stream, not a filter of it. Closed by default so the default
             thread is unchanged — a search box permanently parked above the
             conversation is chrome the reader has to learn to ignore. */}
        {onSearch && (
          <div className="pear-archive" data-archive>
            <button
              type="button"
              className="pear-archive-toggle"
              data-archive-toggle
              aria-expanded={archiveOpen}
              aria-controls="pear-archive-panel"
              onClick={() => {
                const next = !archiveOpen;
                setArchiveOpen(next);
                if (!next) {
                  setArchiveResults(null);
                  setArchiveError(null);
                }
              }}
            >
              {archiveOpen ? 'Close archive' : 'Search the whole thread'}
            </button>

            {archiveOpen && (
              <div className="pear-archive-panel" id="pear-archive-panel">
                <form
                  className="pear-archive-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void runArchiveSearch();
                  }}
                >
                  <input
                    type="search"
                    className="pear-archive-input"
                    data-archive-input
                    value={archiveQuery}
                    /* The window is 7 days, so a word that is not in it is
                       still worth asking about — that is the whole point. */
                    placeholder="A word you remember…"
                    aria-label="Search the whole thread"
                    onChange={(e) => setArchiveQuery(e.target.value)}
                  />
                  <button
                    type="submit"
                    className="pear-btn pear-archive-go"
                    data-archive-go
                    disabled={archiveBusy || archiveQuery.trim().length < 2}
                  >
                    {archiveBusy ? 'Searching…' : 'Search'}
                  </button>
                </form>

                {archiveError && (
                  <p className="pear-archive-error" data-archive-error role="status">{archiveError}</p>
                )}

                {!archiveError && archiveResults && archiveResults.length === 0 && (
                  <p className="pear-archive-empty" data-archive-empty role="status">
                    Nothing matches “{archiveQuery.trim()}” anywhere in your thread.
                  </p>
                )}

                {archiveResults && archiveResults.length > 0 && (
                  <>
                    <p className="pear-archive-count" data-archive-count role="status">
                      {archiveResults.length} match{archiveResults.length === 1 ? '' : 'es'}
                    </p>
                    <ul className="pear-archive-results" data-archive-results>
                      {archiveResults.map((m) => (
                        <li key={`archive-${m.seq}`}>
                          <button
                            type="button"
                            className="pear-archive-result"
                            data-archive-result
                            data-archive-seq={m.seq}
                            onClick={() => jumpToResult(m.seq)}
                          >
                            <span className="pear-archive-when">{dayLabel(m.ts)}</span>
                            {/* Cards render as their payload JSON, which is not
                                something to read in a result list — show the
                                kind instead of the envelope. The excerpt is
                                windowed on the match and the matched run is
                                marked (card vN5IhlgoFXTb): a word late in a
                                long message used to fall outside the first
                                120 characters, so the row looked unrelated to
                                what was typed. */}
                            <span className="pear-archive-excerpt">
                              {m.kind && m.kind !== 'text' ? `[${m.kind.replace(/-/g, ' ')}] ` : ''}
                              {(() => {
                                const ex = searchExcerpt(m.text, archiveQuery);
                                return (
                                  <>
                                    {ex.before}
                                    {ex.match && <mark className="pear-archive-hit">{ex.match}</mark>}
                                    {ex.after}
                                  </>
                                );
                              })()}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* The "Needs your attention" strip is gone (Jonathan 2026-10-17):
            it duplicated cards that are already in the stream — the answer
            card is right there — so a second copy above the stream was noise
            that needed its own reading pass. Card: G2a36nBM74sD. The stream
            is the single place a card waits for you. */}

        {/* ---- Life rail (FiaOoQ0mb11g) ----
            System output above the stream: chores scored to the top three,
            the rest collapsed into "Life (n)". Own block, own heading — it
            never draws as a bubble and never joins the message flow. */}
        <LifeRail nameFor={nameFor} onOpenLife={() => onNavigate?.({ section: 'life', subsection: 'chores' })} />

        {/* ---- The filtered region (UX item 6, sol round 1) ----
             The chips name THIS box, not the scroll container they sit in:
             a tab's aria-controls must resolve to a tabpanel that is not an
             ancestor of its own tablist. The life rail stays outside it —
             it is system output above the stream, not something a filter
             shows or hides. */}
        <div
          id="thread-panel"
          className="sub-view"
          role="tabpanel"
          aria-labelledby={`thread-chip-${activeFilter}`}
          /* A region you can land on: the empty thread has nothing focusable
             inside it, so without this the panel is unreachable by keyboard. */
          tabIndex={0}
        >
          {loading && !hasContent && (
            <div style={{ padding: 'var(--space-4) 0' }}>
              <SkeletonBlock lines={3} />
            </div>
          )}

          {!loading && !hasContent && (
            <div className="pear-chat-empty" id="chat-empty">
              {/* Paired vs solo: once the partner has joined there is no
                  invite left to send, so the empty thread greets the couple
                  instead of sending them back to the pairing screen. */}
              {paired ? (
                <>
                  <p className="pear-chat-empty-text">
                    Nothing here yet — say hello; everything you share lands in this thread.
                  </p>
                  <p className="pear-chat-empty-hint">
                    Notes, asks and photos from both of you show up right here.
                  </p>
                </>
              ) : (
                <>
                  <p className="pear-chat-empty-text">
                    Send your invite — your partner joins and this thread fills up.
                  </p>
                  <p className="pear-chat-empty-hint">
                    Use the invite card above — copy the link, share it, email it, or scan the QR.
                  </p>
                </>
              )}
            </div>
          )}

          {/* Newest at top, so the in-flight echo sits ABOVE the history —
              it is the youngest thing in the thread (card BvSZRPd9uuwW). */}
          {echoes.map((echo) => (
            <div className="pear-msg pear-msg-mine pear-local-echo" key={echo.clientMsgId} data-msg-id={echo.clientMsgId}>
              <div className="pear-msg-bubble pear-msg-bubble-mine pear-msg-echo">
                {echo.text}{' '}
                <span className="pear-msg-sending">(sending…)</span>
              </div>
            </div>
          ))}

          {renderMessages.map((msg, index) => {
            const drop = supersededRequestSeqs.has(msg.seq) || supersededQuestionSeqs.has(msg.seq);
            /* Day separator (card 4ynbWszGdqUd): the stream is newest-at-top, so
               a separator belongs to the first message of each calendar day and
               is decided by comparing against the message ABOVE (index-1). A
               superseded row is dropped entirely, so it never emits a separator —
               the next visible row starts its own day instead. */
            const dayHere = dayKey(msg.ts);
            const showSep = !drop && dayHere !== null && dayHere !== dayKey(renderMessages[index - 1]?.ts);
            return drop ? null : (
              <Fragment key={`wrap-${msg.seq}`}>
                {showSep && (
                  <div className="pear-day-sep" data-day={dayHere ?? ''}>{dayLabel(msg.ts)}</div>
                )}
                {msg.kind === 'kudos' ? (
              <div className="pear-kudos-card" key={`kudos-${msg.seq}`} data-kudos-seq={msg.seq}>
                <span className="pear-kudos-glyph">{KUDOS_GLYPH}</span>
                <div className="pear-kudos-body">
                  <span className="pear-kudos-label">
                    {msg.fromMemberId === myMemberId
                      ? 'You gave kudos'
                      // The display name is the room's own (card yFLCjm7ONIEc);
                      // 'your partner' is what is left when it has not been set.
                      : 'Kudos from ' + (nameFor?.(msg.fromMemberId) ?? 'your partner')}
                  </span>
                  {msg.text && <p className="pear-kudos-text">{msg.text}</p>}
                </div>
                {msg.fromMemberId === myMemberId && echoSeq === msg.seq && <span className="pear-kudos-echo" />}
              </div>
            ) : msg.kind === 'request-card' ? (
              /* Request lifecycle card (2eueFMxFylie): the text column carries
                 JSON {v, requestId, action, requestText}, so the parse is
                 defensive — a malformed payload degrades to a plain bubble
                 instead of throwing inside the render pass. The POSTED card
                 from the partner is the one that can be answered inline; every
                 other action is a quiet status line. */
              <RequestCard
                key={`request-${msg.seq}`}
                seq={msg.seq}
                text={msg.text}
                mine={msg.fromMemberId === myMemberId}
                partnerName={nameFor?.(msg.fromMemberId) ?? 'your partner'}
                role={roleFor(msg.fromMemberId)}
              />
            ) : msg.kind === 'question-card' ? (
              /* Today's question as a typed card (jinkfGNSDI4j): one message per
                 question, rewritten in place when both have answered, so this
                 draws whatever state the payload carries. The parse is
                 defensive for the same reason the request card's is — a
                 malformed payload degrades to a plain bubble. */
              <QuestionCard
                key={`question-${msg.seq}`}
                seq={msg.seq}
                text={msg.text}
                myMemberId={myMemberId}
                nameFor={nameFor}
                onOpenUs={() => onNavigate?.({ section: 'us', subsection: 'questions' })}
              />
            ) : msg.kind === 'date-card' ? (
              /* The locked-date celebration (ZdYBVP203b8a, PRD §6.3): one card,
                 posted by the app when both partners are in. A date card only
                 exists post-lock, so carrying the plan itself leaks nothing —
                 there is no pre-lock state it is ever written in. */
              <DateCard
                key={`date-${msg.seq}`}
                seq={msg.seq}
                text={msg.text}
                onOpenUs={() => onNavigate?.({ section: 'us', subsection: 'dates' })}
              />
            ) : msg.kind === 'agent-card' && parseDateNudge(msg.text) ? (
              /* Cadence nudge (yejoF8uTVxyf): the concierge's one-per-month
                 prompt, couple-addressed, carrying its own deep link into the
                 surface that answers it (PRD §6.3). Concierge prose shares this
                 kind and fails the parse, so it falls through to the plain card
                 below — same defensive shape as QuestionCard/DateCard. */
              <DateNudgeCard
                key={`date-nudge-${msg.seq}`}
                seq={msg.seq}
                text={msg.text}
                onOpenDates={() => onNavigate?.({ section: 'us', subsection: 'dates' })}
              />
            ) : msg.kind === 'agent-card' && msg.roundId ? (
              /* Game-round card (Bkt2clm8SSOI): same agent-card affordance as the
                 concierge, plus the link back to the panel that answers it. The
                 text is rewritten by the server on reveal, so one round is one
                 card here — never an invitation and a result side by side. */
              <div
                /* Card 6gd4aWNsDsqJ: the game card and the question card share
                   one shell (.pear-thread-card) and the same emoji slot, so the
                   two read as one family down the stream. */
                className="pear-thread-card pear-agent-card pear-game-card"
                key={`game-${msg.seq}`}
                data-msg-seq={msg.seq}
                data-game-round={msg.roundId}
              >
                <span className="pear-card-emoji" aria-hidden="true">{GAME_GLYPH}</span>
                <div className="pear-game-body">
                  <p className="pear-agent-copy">{msg.text}</p>
                  {onOpenGameRound && (
                    <button
                      type="button"
                      className="pear-btn pear-btn-mustard pear-game-open"
                      aria-label="Open Back and forth in the Us tab"
                      onClick={() => onOpenGameRound(msg.roundId!)}
                    >
                      Open in Us →
                    </button>
                  )}
                </div>
              </div>
            ) : msg.kind === 'agent-card' ? (
              <div className="pear-agent-card" key={`agent-${msg.seq}`} data-msg-seq={msg.seq}>
                <span className="pear-agent-dot" />
                <p className="pear-agent-copy">{msg.text}</p>
              </div>
            ) : msg.kind === 'system' ? (
              <div className="pear-sys-line" key={`sys-${msg.seq}`} data-msg-seq={msg.seq}>
                {msg.text}
              </div>
            ) : (
              <Bubble
                key={msg.seq}
                seq={msg.seq}
                text={msg.text}
                mine={msg.fromMemberId === myMemberId}
                role={roleFor(msg.fromMemberId)}
                onRemoveMessage={onRemoveMessage}
                /* The sender's chosen reply ink (card 9QADJv3CdiOh) — resolved
                   off the live members list like the name below, so a Settings
                   change shows on the next message without a reload. Own
                   messages never take it: the colour is what the PARTNER sees. */
                colour={msg.fromMemberId === myMemberId || !colourFor ? null : colourFor(msg.fromMemberId)}
                /* Label the partner's messages with their display name, once
                   per run of messages (card yFLCjm7ONIEc) — the name comes
                   from the live members list, so a Settings change shows on
                   the next message without a reload. */
                from={
                  msg.fromMemberId === myMemberId || !nameFor
                    ? null
                    : renderMessages[index - 1]?.fromMemberId === msg.fromMemberId
                      ? null
                      : nameFor(msg.fromMemberId)
                }
              />
            )}
              </Fragment>
            );
          })}

          {/* Windowed hydration (card 4ynbWszGdqUd): a fresh open loads only the
              last 7 days. When the server holds older messages, this affordance
              pages them in (loadOlder) — newest-at-top, so it sits at the bottom
              of the stream where the oldest loaded message is. */}
          {hasOlder && (
            <button
              type="button"
              className="pear-thread-older"
              data-thread-older
              onClick={() => onLoadOlder?.()}
            >
              Show earlier messages
            </button>
          )}
        </div>

        {/* ---- Desktop right context rail (card sUIMDZCfzM0l, A1b) ----
            Renders ONLY when a message is selected AND the >=1100px three-zone
            token is live, so on a phone it is never in the DOM (accept line 1
            and 2). It reuses the notebook .nb-context primitive and floats in
            the right gutter the A1a ~680px cap leaves empty. */}
        {desktopMatch.current?.matches === true && selectedMsg && (
          <aside
            className="nb-context pear-thread-rail"
            data-thread-rail
            aria-label="Message details"
            ref={railRef}
            style={{ ['--pear-thread-rail-w' as string]: 'clamp(280px, 26vw, 360px)' }}
          >
            <div className="nb-context-top">
              <span className="pear-thread-rail-kicker" data-thread-rail-kicker>
                {!selectedMsg.kind || selectedMsg.kind === 'text'
                  ? selectedMsg.fromMemberId === myMemberId
                    ? 'Your message'
                    : 'Message'
                  : `Message · ${selectedMsg.kind.startsWith('request') ? 'ask' : selectedMsg.kind.replace(/-/g, ' ')}`}
              </span>
              <button
                type="button"
                className="nb-context-close"
                data-thread-rail-close
                aria-label="Close message details"
                onClick={() => setSelectedSeq(null)}
              >
                Close
              </button>
            </div>

            {/* Quick-entry tiles (card uLr0sCxQDnd7, A1c) — capture where you
                look: the shipped ComposeMenu tile defs, rendered one click from
                the rail header instead of behind the + menu. Sits AFTER the top
                row so the Close button stays the rail's first focusable element
                (A1b's focus contract). Desktop-only by construction — this whole
                aside never enters the DOM below 1100px, and the stream's pinned
                Tonight card and chrome rows are untouched. */}
            <div
              className="pear-thread-rail-quick"
              data-thread-rail-quick
              role="group"
              aria-label="Quick entry"
            >
              {TILE_DEFS.map((def) => (
                <button
                  key={def.type}
                  type="button"
                  className="pear-thread-rail-quick-tile"
                  data-thread-rail-tile={def.type}
                  title={def.label}
                  onClick={() => handleComposePick(def.type)}
                >
                  <span className="pear-thread-rail-quick-icon" aria-hidden="true">
                    {def.icon}
                  </span>
                  <span className="pear-thread-rail-quick-label">{def.label}</span>
                </button>
              ))}
            </div>

            <div className="pear-thread-rail-msg" data-thread-rail-msg>
              <div className="pear-msg-bubble" data-thread-rail-text>
                {selectedMsg.kind === 'text' ? selectedMsg.text : selectedMsg.text}
              </div>
              {selectedMsg.ts && (
                <p className="nb-context-meta" data-thread-rail-when>
                  {dayLabel(selectedMsg.ts)} · {new Date(selectedMsg.ts).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                </p>
              )}
              <div className="pear-thread-rail-actions" data-thread-rail-actions>
                {/* Save to Notebook — the same route the in-stream affordance
                    hits; rendered here so the rail owns the action. */}
                <MessageSave seq={selectedMsg.seq} text={selectedMsg.text} />
                {/* Only self-authored text rows are removable (the DO enforces
                    it); the rail mirrors the trash affordance. */}
                {selectedMsg.kind === 'text' &&
                  selectedMsg.fromMemberId === myMemberId &&
                  onRemoveMessage && (
                    <button
                      type="button"
                      className="pear-thread-rail-action"
                      data-thread-rail-delete
                      onClick={() => {
                        if (window.confirm('Delete this message for you both?')) {
                          onRemoveMessage(selectedMsg.seq);
                          setSelectedSeq(null);
                        }
                      }}
                    >
                      Delete
                    </button>
                  )}
                {/* Card links — the "Open in Us →" family, one tap from the
                    rail instead of a modal/navigation the stream forced. */}
                {selectedMsg.kind === 'request-card' && onNavigate && (
                  <button type="button" className="pear-thread-rail-action" data-thread-rail-link
                    onClick={() => onNavigate({ section: 'life', subsection: 'requests' })}>
                    Open Requests
                  </button>
                )}
                {selectedMsg.kind === 'question-card' && onNavigate && (
                  <button type="button" className="pear-thread-rail-action" data-thread-rail-link
                    onClick={() => onNavigate({ section: 'us', subsection: 'questions' })}>
                    Open Questions
                  </button>
                )}
                {selectedMsg.kind === 'date-card' && onNavigate && (
                  <button type="button" className="pear-thread-rail-action" data-thread-rail-link
                    onClick={() => onNavigate({ section: 'us', subsection: 'dates' })}>
                    Open Dates
                  </button>
                )}
              </div>
            </div>
          </aside>
        )}

      </div>


      {/* ---- Composer, BOTTOM-docked (card CwbqiUPwRg02, reverses
          dZjN98QXAgEH) ----
          Jonathan 2026-10-17: the entry box "should sit at the BOTTOM,
          be bigger, multiline" — a chat composer belongs where you type,
          under the conversation, not above it. The block renders after
          #thread-scroll, so .pear-chat's column flex seats it at the foot
          of the frame (above the tab bar, which .pear-app's bottom padding
          already clears). Its position:relative is still the containing
          block for ComposeMenu's absolute overlay — which now opens
          UPWARD from this bottom anchor. */}
      <div className="pear-chat-input-area">
        {/* ComposeMenu anchors to this block and opens UPWARD now (the
            anchor sits at the bottom of the frame, card CwbqiUPwRg02) */}
        <ComposeMenu open={menuOpen} onClose={() => setMenuOpen(false)} onPick={handleComposePick} />

        {/* Kudos sender (card V0dtVf36Dplz) — compose-menu deep-link target.
            Card DpcUGvfaDJir: the old in-flow sender floated above the
            composer inside `.pear-chat-input-area`, so on a phone it was
            squeezed into whatever height the thread had left and clipped —
            and it read as a form row, not a gesture. It is a postcard now:
            a backdrop over the whole app and one paper panel portalled to
            body, so no transformed ancestor can trap the overlay and no short
            thread can crop it. Backdrop tap, outside pointer and Escape all
            cancel; focus and the inert background are the shared Overlay's
            contract (UX item 6), not this screen's. */}
        <Overlay
          open={kudosOpen}
          onClose={() => setKudosOpen(false)}
          className="pear-kudos-overlay"
          testId="kudos-overlay"
          style={{ ['--kb-inset' as string]: `${kudosKbInset}px` }}
          panelClassName="pear-kudos-sender"
          label="Give kudos"
          /* The postcard opens FROM the compose menu, which unmounts as it
             opens — so the remembered opener is gone by the time focus has to
             come back. Hand it the + button explicitly. */
          restoreFocusTo=".pear-compose-toggle"
        >
          {/* Stamp corner — the gesture's mark on the card, top-right
              where a postcard stamps. Decorative: the glyph is already
              announced by the dialog's label. */}
          <span className="pear-kudos-stamp" aria-hidden="true">
            <span className="pear-kudos-sender-glyph">{KUDOS_GLYPH}</span>
          </span>

          <p className="pear-kudos-to">For your partner</p>

          <textarea
            className="pear-chat-input pear-kudos-note"
            placeholder="That was lovely — say why (optional)"
            aria-label="Kudos note"
            value={kudosNote}
            autoFocus
            rows={3}
            maxLength={200}
            onChange={e => setKudosNote(e.target.value)}
          />

          <div className="pear-kudos-actions">
            <button
              type="button"
              className="pear-btn"
              aria-label="Cancel kudos"
              onClick={() => setKudosOpen(false)}
            >
              Cancel
            </button>
            {/* The panel is a div now, not a <form> — the Overlay owns the
                panel element, so submission is the button's own handler
                rather than an implicit form submit. */}
            <button
              type="button"
              className="pear-btn pear-btn-mustard"
              disabled={kudosBusy}
              aria-label="Send kudos"
              onClick={() => void sendKudos()}
            >
              {kudosBusy ? 'Sending…' : 'Send'}
            </button>
          </div>
        </Overlay>

        {/* H7 (card ESidDNa3mmaP) — the visible half of a failed action:
            one bottom alert, portalled to body like the update toast. */}
        <ActionErrorToast />

        {/* Received kudos: the burst, or — under prefers-reduced-motion — a
            quiet static line (sol M4). Both are announced politely; the
            particle layer stays decorative and aria-hidden. */}
        <p className="pear-sr-only" role="status" aria-live="polite">
          {celebrateSeq !== null ? 'Kudos from your partner' : ''}
        </p>
        {celebrateSeq !== null &&
          (reduceMotion ? (
            <div className="pear-kudos-toast" data-kudos-toast={celebrateSeq}>
              {KUDOS_GLYPH} Kudos from your partner
            </div>
          ) : (
            <div className="pear-kudos-confetti" aria-hidden="true">
              {Array.from({ length: 14 }).map((_, i) => (
                <span
                  key={i}
                  className="pear-kudos-particle"
                  style={{
                    left: `${8 + (i % 7) * 13}%`,
                    animationDelay: `${(i % 5) * 0.06}s`,
                    ['--kudos-hue' as string]: `${(i * 47) % 360}`,
                  }}
                />
              ))}
            </div>
          ))}

        {/* Card tPPB3ozS4YrK — the partner's keystrokes, in the open thread
            and nowhere else. Announced politely for assistive tech, never
            paired with a delivery or seen tick, and gone on its own after
            TYPING_TTL_MS of silence: it says "right now", never "they read
            it". data-typing-indicator carries the sender's member id so the
            walk can prove the element appears, clears, and never renders when
            the flag is off. */}
        {typingOn && typingFrom && (
          <p className="pear-typing" data-typing-indicator={typingFrom} role="status" aria-live="polite">
            <span className="pear-typing-dots" aria-hidden="true">
              <i /><i /><i />
            </span>
            {nameFor?.(typingFrom) ?? 'Your partner'} is typing…
          </p>
        )}

        {/* Two rows (founder 2026-10-17): the box takes a whole row so it can
            be full width and grow with the draft, and the controls — the +
            toggle, the concierge switch and send — sit under it instead of
            squeezing the field to ~65% of the composer. Shared composer
            surface (card 4DrBwftybJ7q) so the question panel grows the same
            way. */}
        <Composer
          id="chat-input"
          formClassName="pear-composer"
          formId="chat-form"
          inputClassName="pear-chat-input pear-composer-input"
          ariaLabel="Message input"
          placeholder="Type a reply…"
          value={draft}
          maxLength={THREAD_TEXT_MAX}
          onChange={(next) => {
            setDraft(next);
            // Card tPPB3ozS4YrK — a hint only when there is something to hint
            // at. useThread collapses a burst of keystrokes to one frame per
            // 2s, and an empty (or whitespace-only) draft sends nothing at all:
            // backspacing to nothing must not leave the partner watching a
            // sentence that is no longer being written.
            if (typingOn && next.trim().length > 0) onTyping?.();
          }}
          onSubmit={submit}
          textAreaRef={inputRef}
          sendAriaLabel="Send message"
          sendLabel="→"
          sendClassName="pear-btn pear-btn-mustard pear-send"
          controls={
            <>
              <button
                type="button"
                className="pear-btn pear-btn-mustard pear-compose-toggle"
                aria-label="Composing options"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((v) => !v)}
              >
                +
              </button>
              {showChip && (
                /* Card yNo09LssaaFf + DvrFWDcNA9Ix: reads as a SWITCH, not a
                   text pill button — a small track + thumb that slides
                   (aria-pressed carries the state, so walk-agent-chip keeps
                   working). The armed state is PERSISTENT now: it survives a
                   send and a remount, so one tap arms every message until the
                   user taps it off. (Folds 6wz9UQwCi5ND — same toggle,
                   persists per thread.) The track is 34px; the button box is
                   padded to a 44px hit area. */
                <button
                  type="button"
                  id="agent-chip"
                  className={`pear-chip-agent${armed ? ' is-on' : ''}`}
                  aria-pressed={armed}
                  aria-label="Ask the concierge to weigh in on this message"
                  title="Ask the concierge"
                  onClick={() => setArmed(!armed)}
                >
                  {/* Founder email 2026-10-19: sparkles replaces the bulb as the
                      AI glyph (lucide sparkles, inlined — no icon dep). */}
                  <span className="pear-chip-agent-spark" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                      <path d="M20 3v4" /><path d="M22 5h-4" /><path d="M4 17v2" /><path d="M5 18H3" />
                    </svg>
                  </span>
                  <span className="pear-chip-agent-track" aria-hidden="true">
                    <span className="pear-chip-agent-thumb" />
                  </span>
                  {/* Founder email 2026-10-23: the word rides ONLY with the
                      switch on — off, the sparkle + track carry the control
                      and the caption stays out of the row. aria-pressed and
                      the button's name are unchanged, so assistive tech and
                      the walks still read the state. */}
                  {armed && <span className="pear-chip-agent-label">Concierge</span>}
                  {/* Card cyNoIpzWN3sd (Jonathan 2026-10-20): the on/off WORDS
                      are gone. The track and thumb already show which way the
                      switch is set, so spelling the state out beside the glyph
                      was clutter on the one row that has no air to spare.
                      `aria-pressed` still carries the state for assistive
                      tech, and the accessible name/tooltip are unchanged. */}
                </button>
              )}
            </>
          }
        />
      </div>
    </>
  );
}

/**
 * Two-ness: role A = bottle green, role B (or unknown, before members load) =
 * blush. Colours come from `--color-partner-a/b` instead of the hex literals
 * that lived in main.ts.
 */
function Bubble({ text, mine, role, from, seq, colour, onRemoveMessage }: { text: string; mine: boolean; role: Role | null; from?: string | null; seq?: number; colour?: string | null; onRemoveMessage?: (seq: number) => void }) {
  /* One delete path for both affordances (card — msg-remove). Confirm first:
     the row vanishes for BOTH partners, so a stray tap must not remove
     without a native yes/no. */
  const confirmDelete = () => {
    if (seq == null || !onRemoveMessage) return;
    if (!window.confirm('Delete this message for you both?')) return;
    onRemoveMessage(seq);
  };

  /* Card CJQIKjFiNUCW — the controls are an in-message popup, not a strip
     under the bubble. Hover reveals them on a fine pointer; touch has no
     hover, so a tap or long-press sets the same open state explicitly. */
  const [actionsOpen, setActionsOpen] = useState(false);

  useEffect(() => {
    if (!actionsOpen) return;
    const onDown = (e: Event) => {
      if (e.target instanceof Element && e.target.closest('.pear-msg-acts')) return;
      setActionsOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setActionsOpen(false); };
    const onScroll = () => setActionsOpen(false);
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [actionsOpen]);

  const hasActions = seq != null;

  return (
    <div
      className={`pear-msg ${mine ? 'pear-msg-mine' : 'pear-msg-other'}`}
      data-mine={String(mine)}
      data-role={role ?? ''}
      data-from={from ?? ''}
      data-reply-colour={colour ?? ''}
      data-actions={actionsOpen ? 'open' : 'closed'}
      /* Archive jump target (card 2sjo3uNvaRDr): the one handle a jump can
         find after the history page lands. Kept on the row, not the bubble,
         so the highlight covers the whole message. */
      data-msg-seq={seq ?? ''}
      /* Long-press / right-click OPENS the popup (card CJQIKjFiNUCW). It used
         to fire the delete confirm straight off contextmenu, which made a held
         press a destructive shortcut with no visible control. Partner messages
         keep the platform menu — there is nothing to remove there. */
      onContextMenu={hasActions ? (e) => { e.preventDefault(); setActionsOpen(true); } : undefined}
    >
      {from && <span className="pear-msg-from">{from}</span>}
      {/* Card 5KtSIi7Gz8tb (founder email 2026-10-18): the thread reads WHO
          spoke, not which role slot — my words on white, the partner's on
          gold. Both pairs are AA on the velvet ground (cream 11.9:1,
          mustard 5.5:1 against bottle-green ink). data-role stays for the
          probes and the name label above.

          Card 9QADJv3CdiOh: the partner's bubble also wears the ink THEY
          picked (Settings > You). Every offered colour is measured AA on the
          same mustard ground, and the inline colour only lands when a real
          hex arrived — unknown/absent falls back to the token pair untouched. */}
      <div
        className={`pear-msg-bubble ${mine ? 'pear-msg-bubble-mine' : 'pear-msg-bubble-theirs'}`}
        style={colour ? { color: colour } : undefined}
        onClick={
          /* Touch only: a tap on a bubble toggles the popup. On a fine pointer
             hover already owns the reveal, and a click-to-open there would
             fight the text selection.

             Founder email 2026-10-27: this used to be gated on `mine`, so a
             PARTNER's message could no longer be tapped to open it — and pin
             (save to Notebook) is exactly what you want on the other person's
             message. Delete stays scoped to your own bubble below; the popup
             itself is now reachable on either side. */
          hasActions && typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches
            ? (e) => { e.stopPropagation(); setActionsOpen(v => !v); }
            : undefined
        }
      >
        {text}
        {/* Founder email 2026-10-27 (also): with hover there is no affordance
            on touch, so a coarse pointer gets one always-visible "more" chip in
            the bubble's corner. It is display:none on a fine pointer, where
            hover does the job, and it disappears once the popup is open. */}
        {hasActions && !actionsOpen && (
          <button
            type="button"
            className="pear-msg-more"
            aria-label="Message actions"
            aria-expanded={actionsOpen}
            onClick={(e) => { e.stopPropagation(); setActionsOpen(true); }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
              <circle cx="5" cy="12" r="1.6" />
              <circle cx="12" cy="12" r="1.6" />
              <circle cx="19" cy="12" r="1.6" />
            </svg>
          </button>
        )}
        {/* The popup itself (card CJQIKjFiNUCW): icon-only, overlays the
            bubble's top corner so the bubble never resizes, one X vector for
            delete on touch, pin (save to Notebook) beside it. Delete keeps its
            confirm step and the DO still refuses anything not mine. */}
        {hasActions && (
          <span className="pear-msg-acts" role="group" aria-label="Message actions">
            <MessageSave seq={seq as number} text={text} />
            {mine && onRemoveMessage && (
              <button
                type="button"
                className="pear-msg-act pear-msg-delete"
                aria-label="Delete your message"
                title="Delete"
                onClick={(e) => { e.stopPropagation(); confirmDelete(); }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            )}
          </span>
        )}
      </div>
    </div>
  );
}


/**
 * Save a message to the Notebook (card ERmAXm2aLjjV). Two shapes off the same
 * server route, which already owns the unfurl pipeline and the vault-gated
 * classification:
 *   - the text carries a URL -> POST {url}, the link path (links are shared)
 *   - otherwise             -> POST {entryType:'note', body, shared}
 *     where shared:false is 'me' and shared:true is 'us'.
 * Local UI state only — nothing records which messages were saved.
 */
function MessageSave({ seq, text }: { seq: number; text: string }) {
  const [phase, setPhase] = useState<'idle' | 'choosing' | 'busy' | 'saved' | 'error'>('idle');
  const [error, setError] = useState('');
  const url = /\bhttps?:\/\/\S+/.exec(text)?.[0] ?? null;

  const save = async (visibility: 'us' | 'me') => {
    setPhase('busy');
    setError('');
    try {
      if (url) {
        await api('/api/notebook', { body: { url } });
      } else {
        // Card wGXKzRwJE57v — thread saves carry their real origin so the
        // Notebook card renders "from the thread" only for genuinely
        // thread-sourced items, not every typed card. (The {url} link path
        // below already tags itself through enrich/provider detection.)
        await api('/api/notebook', { body: { entryType: 'note', body: text, shared: visibility === 'us', provider: 'thread' } });
      }
      setPhase('saved');
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : '');
      setPhase('error');
      console.error('[Pear] save to notebook failed', err);
      reportActionFailure('save that to the Notebook');
    }
  };

  if (phase === 'saved') {
    return <span className="pear-msg-saved" data-msg-saved={seq}>Saved to Notebook</span>;
  }
  if (phase === 'error') {
    return (
      <span className="pear-msg-save-error" data-msg-save-error={seq}>
        Could not save{error ? ` (${error})` : ''}
        <button type="button" className="pear-msg-save" onClick={() => setPhase('choosing')}>Try again</button>
      </span>
    );
  }
  if (phase === 'busy') {
    return <span className="pear-msg-save-busy" data-msg-saving={seq}>Saving…</span>;
  }
  if (phase === 'choosing') {
    // One URL, one path: the link route is always shared, so a link message
    // offers no "just me" option.
    return (
      <span className="pear-msg-save-choice" data-msg-save-choice={seq}>
        {url ? (
          <button type="button" className="pear-msg-save-opt" onClick={() => void save('us')}>
            Save link to Notebook
          </button>
        ) : (
          <>
            <button type="button" className="pear-msg-save-opt" onClick={() => void save('us')}>Save for us</button>
            <button type="button" className="pear-msg-save-opt" onClick={() => void save('me')}>Just you</button>
          </>
        )}
        <button type="button" className="pear-msg-save-cancel" onClick={() => setPhase('idle')}>Cancel</button>
      </span>
    );
  }
  return (
    <button
      type="button"
      className="pear-msg-save"
      data-msg-save={seq}
      aria-label="Save to Notebook"
      title="Save to Notebook"
      onClick={() => setPhase('choosing')}
    >
      {/* Card QR9phRSRQHNx — the save affordance is a pushpin glyph, not the
          word "Save"; every icon in the app is a vector (card svmgRALhXjiq). */}
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <path d="M12 17v5" />
        <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />
      </svg>
    </button>
  );
}

/**
 * Request card (card 2eueFMxFylie) — renderer only, the state machine lives
 * in the request routes. The payload rides in the text column as JSON, so
 * parsing is defensive: anything that is not our shape renders as a plain
 * bubble rather than crashing the whole message list.
 */
interface RequestCardPayload {
  requestId: string;
  action: 'posted' | 'accepted' | 'countered' | 'declined';
  requestText: string;
}

function parseRequestCard(raw: string): RequestCardPayload | null {
  if (typeof raw !== 'string') return null;
  try {
    const p = JSON.parse(raw) as Record<string, unknown> | null;
    if (!p || typeof p !== 'object') return null;
    const { requestId, action, requestText } = p;
    if (typeof requestId !== 'string' || requestId.length === 0) return null;
    if (action !== 'posted' && action !== 'accepted' && action !== 'countered' && action !== 'declined') return null;
    if (typeof requestText !== 'string') return null;
    return { requestId, action, requestText };
  } catch {
    return null;
  }
}

function RequestCard({ seq, text, mine, partnerName, role }: {
  seq: number;
  text: string;
  mine: boolean;
  partnerName: string;
  role: Role | null;
}) {
  // Hooks first — the malformed-payload early return below must not change
  // the hook order between renders.
  const [busy, setBusy] = useState<'accept' | 'decline' | null>(null);
  const payload = parseRequestCard(text);
  // One-tap Today (hSgJtaF6deWm): start this card's interaction clock on
  // mount so the accept/decline below can report how long the card sat
  // painted before the tap actually completed.
  useEffect(() => {
    markTodayCardVisible('request', seq);
  }, [seq]);

  if (!payload) return <Bubble text={text} mine={mine} role={role} />;

  const transition = async (action: 'accept' | 'decline') => {
    if (busy) return;
    setBusy(action);
    try {
      // Existing route, existing helper — the socket fan-out delivers the
      // next card, so the buttons only have to survive their own flight.
      await api(`/api/requests/${payload.requestId}/transition`, { body: { action } });
      logTodayAction('request', seq, action);
    } catch (e) {
      console.error('[Pear] request transition failed', e);
      reportActionFailure('update the request');
    } finally {
      setBusy(null);
    }
  };

  // accepted | countered | declined: one quiet line, no buttons.
  if (payload.action !== 'posted') {
    const who = mine ? 'You' : partnerName;
    const verb = payload.action === 'accepted' ? 'accepted' : payload.action === 'countered' ? 'countered' : 'declined';
    return (
      <div
        className="pear-request-card is-status"
        key={`request-${seq}`}
        data-request-card-seq={seq}
        data-request-action={payload.action}
      >
        <p className="pear-request-status">{`${who} ${verb}: ${payload.requestText}`}</p>
      </div>
    );
  }

  // posted: the ask, plus the inline answer — but never on your own ask.
  const showActions = !mine;
  return (
    <div className="pear-request-card" key={`request-${seq}`} data-request-card-seq={seq} data-request-action="posted">
      <div className="pear-request-body">
        <span className="pear-request-label">
          {mine ? 'You asked' : `Request from ${partnerName}`}
        </span>
        <p className="pear-request-text">{payload.requestText}</p>
        {showActions && (
          <div className="pear-request-actions">
            <button
              type="button"
              className="pear-btn pear-btn-mustard"
              data-request-act="accept"
              disabled={busy !== null}
              onClick={() => void transition('accept')}
            >
              {busy === 'accept' ? 'Accepting…' : 'Accept'}
            </button>
            <button
              type="button"
              className="pear-btn"
              data-request-act="decline"
              disabled={busy !== null}
              onClick={() => void transition('decline')}
            >
              {busy === 'decline' ? 'Declining…' : 'Decline'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Question card (card jinkfGNSDI4j) — today's question as a typed Thread card.
 * The backend writes ONE message per question and rewrites it in place when
 * both have answered, so this just draws the payload it receives: pending
 * means answer inline and show only WHO has answered (never their text);
 * revealed puts both lines in place with a way back to the history in Us.
 * The payload rides in the text column, so the parse is defensive.
 */
interface QuestionCardPayload {
  questionId: string;
  question: string;
  state: 'pending' | 'revealed';
  answeredBy: string[];
  answers?: { memberId: string; text: string; late?: boolean }[];
}

function parseQuestionCard(raw: string): QuestionCardPayload | null {
  if (typeof raw !== 'string') return null;
  try {
    const p = JSON.parse(raw) as Record<string, unknown> | null;
    if (!p || typeof p !== 'object') return null;
    const { questionId, question, state, answeredBy } = p;
    if (typeof questionId !== 'string' || questionId.length === 0) return null;
    if (typeof question !== 'string') return null;
    if (state !== 'pending' && state !== 'revealed') return null;
    const by = Array.isArray(answeredBy)
      ? answeredBy.filter((m): m is string => typeof m === 'string')
      : [];
    const rawAnswers = (p as { answers?: unknown }).answers;
    const answers = Array.isArray(rawAnswers)
      ? rawAnswers.filter((a): a is { memberId: string; text: string; late?: boolean } =>
          !!a && typeof a === 'object' &&
          typeof (a as { memberId?: unknown }).memberId === 'string' &&
          typeof (a as { text?: unknown }).text === 'string')
      : undefined;
    return { questionId, question, state, answeredBy: by, answers };
  } catch {
    return null;
  }
}

function QuestionCard({ seq, text, myMemberId, nameFor, onOpenUs }: {
  seq: number;
  text: string;
  myMemberId: string | null;
  nameFor?: (memberId: string | null | undefined) => string | null;
  onOpenUs?: () => void;
}) {
  // Hooks first — the malformed-payload early return below must not change
  // the hook order between renders (same rule as RequestCard).
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const payload = parseQuestionCard(text);
  // One-tap Today (hSgJtaF6deWm): start the interaction clock on mount.
  useEffect(() => {
    markTodayCardVisible('question', seq);
  }, [seq]);

  if (!payload) return <Bubble text={text} mine={false} role={null} />;

  const iAnswered = myMemberId ? payload.answeredBy.includes(myMemberId) : false;
  const partnerAnswered = payload.answeredBy.some(id => id !== myMemberId);

  const label = (memberId: string) =>
    memberId === myMemberId ? 'You' : nameFor?.(memberId) ?? 'Your partner';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const value = draft.trim();
    if (!value || busy) return;
    setBusy(true);
    setError('');
    try {
      await api(`/api/questions/${payload.questionId}/answer`, { body: { text: value } });
      setDraft('');
      logTodayAction('question', seq, 'answer');
      // Solo accumulation (card Pn1wrppZMWEr): this answer may be the one
      // that tops the sealed stack — tell it to re-count.
      window.dispatchEvent(new CustomEvent('pear:questions-changed'));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Your Answer Did Not Save');
      console.error('[Pear] question answer failed', err);
      reportActionFailure('save your answer');
    } finally {
      setBusy(false);
    }
  };

  if (payload.state === 'revealed') {
    const answers = payload.answers ?? [];
    /* A reveal nobody is in is not a card worth drawing (2026-10-17 email):
       zero answers means the day passed with nothing said, and the empty
       "Neither of you answered this one" card read as the question haunting
       the stream. It disappears — the next question lands in its place. */
    if (answers.length === 0) return null;
    /* A one-sided reveal used to read as if both were in. It now says who
       answered and that their response is what is being revealed — the
       partner's absence is stated, never implied to be a presence.
       `label()` maps the reader to the bare word "You", which does not carry a
       verb ("You has answered"), so branch on first person and name the
       partner the second person way (found by _pear_reveal_probe.mjs). */
    const answerer = answers.length === 1
      ? (answers[0].memberId === myMemberId
          ? 'You have answered'
          : `${label(answers[0].memberId)} has answered`)
      : null;
    return (
      <div
        className="pear-thread-card pear-question-card is-revealed"
        data-question-card-seq={seq}
        data-question-state="revealed"
        /* Today's questionId is exposed so a probe can anchor to THIS card:
           Thread.tsx keeps the highest-seq card, and a dev-seeded past
           question (walk fixture) outranks it by seq. Without this the probe
           measures the fixture and reports today's card as broken. */
        data-question-id={payload.questionId}
      >
        <span className="pear-card-emoji" aria-hidden="true">{QUESTION_GLYPH}</span>
        {/* Body wrapper aligns the question card's column with the game
            card's .pear-game-body, so the two cards' text starts on the same
            row. Indentation of the children is left alone on purpose — this
            commit is the shell, not a whitespace sweep. */}
        <div className="pear-card-body">
        <p className="pear-question-label">Today's question</p>
        <p className="pear-question-text">{payload.question}</p>
        {/* Who is actually in the reveal, said out loud (2026-10-17 email).
            Both answered reads as the ceremony; one answered names that
            person and reveals only their response. */}
        {answerer && (
          <p className="pear-question-scope" data-question-scope="one">
            {answerer} — revealing their response.
          </p>
        )}
        <div className="pear-question-answers">
          {/* Founder email 2026-10-27: the reveal card attached to the thread
              uses the SAME implementation as the full reveal history — the
              shared RevealPair (bounded columns, pinned labels, measured
              clamp). It used to stack two loose answer blocks here, so the
              same pair of answers read differently depending on where you
              looked. */}
          <RevealPair
            answers={answers}
            order={myMemberId ? [myMemberId, ...answers.map(a => a.memberId).filter(id => id !== myMemberId)] : answers.map(a => a.memberId)}
            nameFor={label}
            testId="thread-reveal"
          />
        </div>
        {/* FOUNDING CHANGE (email 2026-10-21): the thread no longer hosts its
            own question text entry. One answer pill routes to the dedicated
            question entry in Us → Questions, so the answer is written in one
            place and the home thread stays a stream. Late answering still
            works — the Questions panel keeps its own late composer. */}
        {!iAnswered && onOpenUs && (
          <button
            type="button"
            className="pear-btn pear-question-submit pear-question-answer-pill"
            data-test="question-answer-pill"
            onClick={onOpenUs}
          >
            Answer in Questions →
          </button>
        )}
        {onOpenUs && (
          <button type="button" className="pear-question-link" onClick={onOpenUs}>
            See history in Us
          </button>
        )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="pear-thread-card pear-question-card"
      data-question-card-seq={seq}
      data-question-state="pending"
    >
      <span className="pear-card-emoji" aria-hidden="true">{QUESTION_GLYPH}</span>
      <div className="pear-card-body">
      <p className="pear-question-label">Today's question</p>
      <p className="pear-question-text">{payload.question}</p>
      <div className="pear-question-markers">
        <span className={`pear-question-marker${iAnswered ? ' is-answered' : ''}`}>
          You: {iAnswered ? 'answered' : 'not yet'}
        </span>
        <span className={`pear-question-marker${partnerAnswered ? ' is-answered' : ''}`}>
          Your partner: {partnerAnswered ? 'answered' : 'not yet'}
        </span>
      </div>
      {iAnswered ? (
        <p className="pear-question-waiting">Locked in — it opens when they answer, or after a day.</p>
      ) : onOpenUs ? (
        /* Same pill as the revealed branch (email 2026-10-21): the thread
           carries no question text entry at all now. */
        <button
          type="button"
          className="pear-btn pear-question-submit pear-question-answer-pill"
          data-test="question-answer-pill"
          onClick={onOpenUs}
        >
          Answer in Questions →
        </button>
      ) : (
        <form className="pear-question-form" onSubmit={submit}>
          <input
            className="pear-question-input"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="Your answer…"
            maxLength={400}
            aria-label="Your answer to today's question"
            disabled={busy}
          />
          <button
            type="submit"
            className="pear-btn pear-question-submit"
            disabled={busy || draft.trim().length === 0}
          >
            {busy ? 'Saving…' : 'Answer'}
          </button>
        </form>
      )}
      {error && <p className="pear-question-error">{error}</p>}
      </div>
    </div>
  );
}

/**
 * Locked-date card (card ZdYBVP203b8a, PRD §6.3) — "You're both in".
 *
 * The server posts exactly one of these, on the affirm that closes the
 * round, so there is no pending state to draw and no answer that could
 * ride it early: a date card only exists once the date is LOCKED. The
 * payload lives in the text column like every other typed card, so the
 * parse is defensive — a malformed payload degrades to a plain bubble.
 */
interface DateCardPayload {
  ideaId: string;
  title: string;
  placeName: string | null;
  mode: string;
  costBand: string;
  energy: string;
  calendarStatus: string;
}

function parseDateCard(raw: string): DateCardPayload | null {
  if (typeof raw !== 'string') return null;
  try {
    const p = JSON.parse(raw) as Record<string, unknown> | null;
    if (!p || typeof p !== 'object') return null;
    const { ideaId, title, placeName, mode, costBand, energy, calendarStatus } = p;
    if (typeof ideaId !== 'string' || ideaId.length === 0) return null;
    if (typeof title !== 'string' || title.length === 0) return null;
    return {
      ideaId,
      title,
      placeName: typeof placeName === 'string' && placeName ? placeName : null,
      mode: typeof mode === 'string' ? mode : 'stay_in',
      costBand: typeof costBand === 'string' ? costBand : '$$',
      energy: typeof energy === 'string' ? energy : 'med',
      calendarStatus: typeof calendarStatus === 'string' ? calendarStatus : 'none',
    };
  } catch {
    return null;
  }
}

const DATE_MODE_SHORT: Record<string, string> = { stay_in: 'In', go_out: 'Out', virtual: 'Virtual' };

function DateCard({ seq, text, onOpenUs }: {
  seq: number;
  text: string;
  onOpenUs?: () => void;
}) {
  // Hooks first — the malformed-payload early return must not change hook
  // order between renders (same rule as RequestCard and QuestionCard).
  const [busy, setBusy] = useState(false);
  const [rainedOut, setRainedOut] = useState(false);
  // One-tap Today (hSgJtaF6deWm): a locked date with no time used to tell
  // you to go to Us → Dates to set one — a navigation for an action this card
  // can take itself. The picker below sets it here, and `calStatus` lets the
  // card flip its own calendar line in place instead of waiting for a reload
  // it never triggers.
  const [calStatus, setCalStatus] = useState<string | null>(null);
  const [whenValue, setWhenValue] = useState('');
  const payload = parseDateCard(text);
  // Start this card's interaction clock on mount (see lib/todayMetrics.ts).
  useEffect(() => {
    markTodayCardVisible('date', seq);
  }, [seq]);

  if (!payload) return <Bubble text={text} mine={false} role={null} />;

  const meta = [payload.costBand === 'free' ? 'Free' : payload.costBand, DATE_MODE_SHORT[payload.mode] ?? payload.mode]
    .filter(Boolean)
    .join(' · ');

  /** PRD §6.2c — a raincheck reschedules without blame, so the copy never
   *  asks why. One tap; the card closes itself rather than re-fetching. */
  const raincheck = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api(`/api/dates/${payload.ideaId}/outcome`, { body: { outcome: 'raincheck' } });
      setRainedOut(true);
      logTodayAction('date', seq, 'raincheck');
    } catch (err) {
      console.error('[Pear] date raincheck failed', err);
      reportActionFailure('reschedule the date');
    } finally {
      setBusy(false);
    }
  };

  /** Set (or move) the time from the card itself. Same route Us → Dates
   *  calls; only the surface is new. datetime-local sends local wall time,
   *  so the epoch-ms conversion happens here — once, matching Dates.tsx. */
  const setWhen = async () => {
    const ms = new Date(whenValue).getTime();
    if (busy || !Number.isFinite(ms)) return;
    setBusy(true);
    try {
      const view = await api<{ calendarStatus?: string }>(`/api/dates/${payload.ideaId}/when`, {
        body: { when: ms },
      });
      // Without Google connected the write stays unsent, and saying
      // "going on the calendar" would be a lie — take the server's word.
      setCalStatus(view?.calendarStatus ?? 'needs_time');
      logTodayAction('date', seq, 'set_time');
    } catch (err) {
      console.error('[Pear] date set time failed', err);
      reportActionFailure('set the time');
    } finally {
      setBusy(false);
    }
  };

  const status = calStatus ?? payload.calendarStatus;
  const needsTime = status === 'needs_time' && !rainedOut;

  return (
    <div className="pear-date-card" data-date-card-seq={seq} data-date-idea={payload.ideaId}>
      <p className="pear-date-kicker">You're both in</p>
      <p className="pear-date-title">{payload.title}</p>
      {payload.placeName && <p className="pear-date-place">{payload.placeName}</p>}
      <p className="pear-date-meta">{meta}</p>
      <p className="pear-date-cal" data-calendar={status}>
        {status === 'written'
          ? 'On the calendar'
          : status === 'queued'
            ? 'Going on the calendar'
            : needsTime
              ? 'No time yet — pick one below and we will put it on the calendar'
              : 'Not on the calendar yet — connect Google and we will add it'}
      </p>
      {needsTime && (
        /* One-tap Today (hSgJtaF6deWm): the action stays on the card. */
        <div className="pear-date-when" data-testid="date-set-when">
          <input
            type="datetime-local"
            className="pear-date-when-input"
            aria-label="When is this date"
            value={whenValue}
            onChange={e => setWhenValue(e.target.value)}
            disabled={busy}
          />
          <button
            type="button"
            className="pear-btn pear-btn-mustard pear-date-when-set"
            disabled={busy || !whenValue}
            onClick={() => void setWhen()}
          >
            {busy ? 'Saving…' : 'Set time'}
          </button>
        </div>
      )}
      {rainedOut ? (
        <p className="pear-date-done" data-testid="date-rained-out">
          Rained out — we will find a better time.
        </p>
      ) : (
        <div className="pear-date-actions">
          <button type="button" className="pear-btn pear-btn-blush pear-date-raincheck" onClick={() => void raincheck()} disabled={busy}>
            {busy ? 'Saving…' : 'Raincheck'}
          </button>
          {onOpenUs && (
            <button type="button" className="pear-date-link" onClick={onOpenUs}>
              Open in Us →
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Cadence nudge card (card yejoF8uTVxyf, PRD §6.3/§9): "Agent suggestion
 * nudges arrive in Thread as deep links into the card, not as a chat message
 * to reply to."
 *
 * One-tap Today (hSgJtaF6deWm): the deep link alone failed the card's
 * acceptance criterion — the nudge's core action is "pick something for this
 * window", and sending you to Us → Dates to do it made it a
 * navigation-required action. So the nudge now carries four quick-picks that
 * capture a date idea here, in place, and the trip to Us → Dates becomes the
 * way you refine it, not the way you start it.
 *
 * The four are drawn from the hand-curated library (shared/date-templates.ts)
 * and chosen for a nudge's context: every one is low-effort or free, because
 * a couple being nudged on cadence does not need a $$$ project suggested at
 * it. A malformed payload degrades to a plain bubble, exactly like the cards
 * above.
 */
const NUDGE_QUICK_PICK_KEYS = [
  'game-card-tournament',
  'cook-taco-bar',
  'game-trivia-night',
  'movie-double-feature',
] as const;

function DateNudgeCard({ seq, text, onOpenDates }: {
  seq: number;
  text: string;
  onOpenDates?: () => void;
}) {
  // Hooks first — the malformed-payload early return must not change hook
  // order between renders (same rule as the cards above).
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const payload = parseDateNudge(text);
  // Start this card's interaction clock on mount (see lib/todayMetrics.ts).
  useEffect(() => {
    markTodayCardVisible('nudge', seq);
  }, [seq]);

  if (!payload) return <Bubble text={text} mine={false} role={null} />;

  const picks = NUDGE_QUICK_PICK_KEYS
    .map(key => DATE_TEMPLATES.find(t => t.key === key))
    .filter((t): t is DateTemplate => !!t);

  /** Capture the chosen template as a real date idea — the same POST the
   *  Dates panel's composer sends, so a quick-pick is a capture, not a
   *  separate kind of object. The card confirms in place; both of you see it
   *  in Us → Dates on the next load. */
  const quickPick = async (t: DateTemplate) => {
    if (busyKey) return;
    setBusyKey(t.key);
    try {
      await api('/api/dates', {
        body: {
          title: t.title,
          note: t.note,
          mode: t.mode,
          costBand: t.costBand,
          energy: t.energy,
          source: 'nudge',
        },
      });
      setPicked(t.title);
      logTodayAction('nudge', seq, 'quick_pick');
    } catch (err) {
      console.error('[Pear] nudge quick-pick failed', err);
      reportActionFailure('add that to your plan');
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div
      className="pear-agent-card pear-date-nudge"
      data-date-nudge-seq={seq}
      data-date-nudge-window={payload.window}
    >
      <span className="pear-agent-dot" />
      <div className="pear-date-nudge-body">
        <p className="pear-agent-copy">{payload.copy}</p>
        {picked ? (
          /* One-tap Today (hSgJtaF6deWm): the action completed on the card,
             so the card says so rather than staying an unanswered prompt. */
          <p className="pear-date-nudge-picked" data-testid="date-nudge-picked">
            Added: {picked} — find it in Us → Dates.
          </p>
        ) : (
          <div className="pear-date-nudge-picks" role="group" aria-label="Quick date picks">
            {picks.map(t => (
              <button
                key={t.key}
                type="button"
                className="pear-btn pear-date-nudge-pick"
                data-nudge-pick={t.key}
                disabled={busyKey !== null}
                onClick={() => void quickPick(t)}
              >
                {busyKey === t.key ? 'Adding…' : t.title}
              </button>
            ))}
          </div>
        )}
        {onOpenDates && (
          <button
            type="button"
            className="pear-date-link pear-date-nudge-open"
            aria-label="Open Dates in the Us tab"
            onClick={onOpenDates}
          >
            See all date ideas →
          </button>
        )}
      </div>
    </div>
  );
}

// --- Helper array for filter chips ---
const filterChips = [
  { key: 'all' as const, label: 'All', disabled: false },
  { key: 'chat' as const, label: 'Chat', disabled: false },
  /* Live as of sol item 10 — they are filters, not promises. */
  { key: 'asks' as const, label: 'Asks', disabled: false },
  { key: 'questions' as const, label: 'Questions', disabled: false },
];
